import { Routes } from "@angular/router";
import { loadInitialDataGuard } from "./core/guards/load-initial-data.guard";
import { loadOpsInitialDataGuard } from "./core/guards/load-ops-initial-data.guard";
import { redirectToFirstTimeUserExperienceGuard } from "./core/guards/redirect-to-first-time-user-experience.guard";
import { BlankComponent } from "./layouts/blank/blank.component";
import { FullComponent } from "./layouts/full/full.component";
import { AuthGuard } from "./services/auth.guard";

import { CandidateClaimsFoundResolver } from "./pages/onboarding/view/candidate-claims-found.resolver";
import { translationResolver } from "./shared/resolvers/translation.resolver";
import { profileResolver } from "./pages/profile/profile.resolver";
import { createRouteData } from "./shared/helpers";
import { DashboardComponent } from "./pages/dashboard/dashboard.component";
import { dashboardResolver } from "./pages/dashboard/dashboard.resolver";
import { billingResolver } from "./pages/billing/billing.resolver";
import { usageOverviewResolver } from "./pages/billing/usage-overview/usage-overview.resolver";
import { blockedNavItemsGuard } from "./core/guards/blocked-nav-items.guard";
import { preventInitialSetupAccessGuard } from "./core/guards/prevent-initial-setup-access.guard";
import { userBillingGuard } from "./core/guards/user-billing.guard";
import { activateServiceBillingGuard } from "./core/guards/activate-service-billing.guard";
import { opsAdminGuard } from "./pages/ops-ui/guards/ops-admin.guard";
export const routes: Routes = [
  {
    path: "activate-service",
    component: BlankComponent,
    canActivate: [AuthGuard, loadInitialDataGuard],
    children: [
      {
        path: "billing",
        canActivate: [activateServiceBillingGuard],
        loadComponent: () =>
          import(
            "./pages/billing/activate-service-billing/activate-service-billing.component"
          ).then((m) => m.ActivateServiceBillingComponent),
        resolve: {
          billingData: billingResolver,
          translations: translationResolver("billing"),
        },
      },
    ],
  },
  {
    path: "initial-setup",
    component: BlankComponent,
    canActivate: [AuthGuard, loadInitialDataGuard],
    canActivateChild: [preventInitialSetupAccessGuard],

    children: [
      {
        path: "welcome",
        loadComponent: () =>
          import("./pages/welcome-animated/welcome-animated.component").then(
            (m) => m.WelcomeAnimatedComponent,
          ),
        resolve: {
          translations: translationResolver("welcome-animated"),
        },
      },
      {
        path: "candidate-claims-found",
        loadComponent: () =>
          import(
            "./pages/onboarding/view/candidate-claims-found.component"
          ).then((m) => m.CandidateClaimsFoundComponent),
        runGuardsAndResolvers: "paramsOrQueryParamsChange",
        resolve: {
          translations: translationResolver("candidate-claims-found"),
          candidateClaimsFoundData: CandidateClaimsFoundResolver,
        },
      },
      {
        path: "billing",
        loadComponent: () =>
          import(
            "./pages/onboarding/view/initial-setup-billing.component"
          ).then((m) => m.InitialSetupBillingComponent),
        resolve: {
          billingData: billingResolver,
          translations: translationResolver("billing"),
        },
      },
    ],
  },
  {
    path: "help",
    component: BlankComponent,
    loadChildren: () =>
      import("./pages/help-center/public-help.routes").then(
        (m) => m.PublicHelpRoutes,
      ),
  },
  {
    path: "ops",
    canActivate: [AuthGuard, loadOpsInitialDataGuard, opsAdminGuard],
    loadChildren: () =>
      import("./pages/ops-ui/ops-ui.routes").then((m) => m.opsUiRoutes),
  },
  {
    path: "",
    component: FullComponent,
    canActivate: [AuthGuard, loadInitialDataGuard],
    canActivateChild: [
      redirectToFirstTimeUserExperienceGuard,
      userBillingGuard,
      blockedNavItemsGuard,
    ],
    children: [
      {
        path: "",
        redirectTo: "/dashboard",
        pathMatch: "full",
        data: {
          ...createRouteData(
            "dashboard",
            "Dashboard",
            "browser",
            "Dashboard",
            "Dashboard",
          ),
        },
      },

      {
        path: "dashboard",
        component: DashboardComponent,
        runGuardsAndResolvers: "always",
        resolve: {
          dashboardData: dashboardResolver,
          translations: translationResolver(["dashboard", "stores"]),
        },
        data: {
          title: "Dashboard",
          urls: [{ title: "Dashboard", url: "/dashboard" }],
          ...createRouteData(
            "dashboard",
            "Dashboard",
            "browser",
            "Dashboard",
            "Dashboard",
          ),
        },
      },
      {
        path: "store",
        loadChildren: () =>
          import("./pages/store-management/store-management.routes").then(
            (m) => m.StoreRoutes,
          ),
      },
      {
        path: "claim",
        loadChildren: () =>
          import("./pages/claim.routes").then((m) => m.ClaimRoutes),
      },
      {
        path: "report",
        loadChildren: () =>
          import("./pages/reports/report.routes").then((m) => m.ReportRoutes),
      },
      {
        path: "billing",
        loadComponent: () => import("./pages/billing/billing.component"),
        resolve: {
          billingData: billingResolver,
          translations: translationResolver("billing"),
        },
        data: {
          ...createRouteData(
            "billing",
            "Billing",
            "browser",
            "Billing",
            "Billing",
          ),
        },
      },
      {
        path: "usage-overview",
        loadComponent: () => import("./pages/billing/usage-overview/usage-overview.component"),
        resolve: {
          usageOverviewData: usageOverviewResolver,
          translations: translationResolver("billing"),
        },
        data: {
          title: "Usage Overview",
          urls: [
            { title: "Dashboard", url: "/dashboard" },
            { title: "Usage Overview", url: "/usage-overview" },
          ],
          ...createRouteData(
            "usage-overview",
            "Usage Overview",
            "browser",
            "Usage Overview",
            "Usage Overview",
          ),
        },
      },
      {
        path: "profile",
        loadComponent: () => import("./pages/profile/profile.component"),
        resolve: {
          profileData: profileResolver,
          translations: translationResolver("profile"),
        },
        data: {
          title: "Profile",
          urls: [{ title: "Profile", url: "/profile" }],
          ...createRouteData(
            "profile",
            "Profile",
            "browser",
            "Profile",
            "Profile",
          ),
        },
      },
    ],
  },
  {
    path: "error",
    component: BlankComponent,
    children: [
      {
        path: "",
        loadComponent: () => import("./pages/error/error.component"),
        resolve: { translations: translationResolver("error") },
      },
    ],
  },
  {
    path: "legal",
    component: BlankComponent,
    loadChildren: () =>
      import("./pages/legal/legal.routes").then((m) => m.LegalRoutes),
  },
  {
    path: "auth",
    component: BlankComponent,
    loadChildren: () => import("./pages/authentication/authentication.routes"),
  },
  {
    path: "stores",
    redirectTo: "/store/stores?invitations=pending",
    pathMatch: "full",
  },
  {
    path: "**",
    redirectTo: "/error",
  },
];
